import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Dialog, DialogContent, Slide } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { QuotationService } from "../../services/quotations.service";

import QuotationTable from "../../components/quotations/QuotationTable";
import QuotationWizard from "../../components/quotations/wizard/QuotationWizard";
import PdfTemplate from "../../components/quotations/wizard/PdfTemplate";
import BusinessEngagementTemplate from "../../components/quotations/wizard/BusinessEngagementTemplate";
import { PdfService } from "../../services/pdf.service";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import { useAuth } from "../../hooks/useAuth";

// Transition for full screen dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const QuotationManagementPage = () => {
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [editingQuote, setEditingQuote] = useState(null);
    const [downloadingQuote, setDownloadingQuote] = useState(null);
    const downloadRef = React.useRef(null);
    const { user } = useAuth();
    
    // For hidden PDF generation on "Preview"
    // We might need a hidden Wizard instance or specific PDF component ref to generate from Table action.
    // Simplifying: The Table actions usually "View" which opens Wizard. 
    // "Download" from table might need acts similarly: pop up render, download, close.

    // Trigger download when template is ready
    useEffect(() => {
        const triggerDownload = async () => {
            if (downloadingQuote && downloadRef.current) {
                try {
                    await PdfService.downloadPdf(downloadRef.current, `Quotation-${downloadingQuote.client?.clientName || 'Export'}.pdf`);
                    await QuotationService.markDownloaded(downloadingQuote.id);
                    fetchQuotations();
                } catch (err) {
                    console.error("Download failed", err);
                    alert("Failed to generate PDF");
                } finally {
                    setDownloadingQuote(null);
                }
            }
        };
        
        if (downloadingQuote) {
            // Short delay to ensure DOM is rendered
            const timer = setTimeout(triggerDownload, 500);
            return () => clearTimeout(timer);
        }
    }, [downloadingQuote]);

    const fetchQuotations = async () => {
        setLoading(true);
        try {
            const data = await QuotationService.listQuotations();
            setQuotations(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotations();
    }, []);

    const handleCreateNew = () => {
        setEditingQuote(null);
        setWizardOpen(true);
    };

    const handleSaveDraft = async (quoteData) => {
        try {
            if (editingQuote) {
               await QuotationService.updateQuotation(editingQuote.id, quoteData);
            } else {
               await QuotationService.createQuotation({
                   ...quoteData,
                   createdByRole: user?.role,
                   createdById: user?.id
               });
            }
            setWizardOpen(false);
            fetchQuotations();
        } catch (e) {
            alert("Failed to save draft");
        }
    };
    
    const handleFinish = async (quoteData, downloaded) => {
        try {
            let id = editingQuote?.id;
            if (id) {
               await QuotationService.updateQuotation(id, quoteData);
            } else {
               const res = await QuotationService.createQuotation({
                   ...quoteData,
                   createdByRole: user?.role,
                   createdById: user?.id
               });
               id = res.id;
            }
            
            if (downloaded) {
                await QuotationService.markDownloaded(id);
            }
            
            setWizardOpen(false);
            fetchQuotations();
        } catch (e) {
            alert("Failed to save/finish quotation");
        }
    };

    const handleAction = async (action, quote) => {
        if (action === 'send') {
            alert("Email service not configured yet.");
            // await QuotationService.sendQuotation(quote.id);
            // fetchQuotations();
        } else if (action === 'view' || action === 'edit') {
            // Need to fetch full detail? mockDb returns full object list.
            setEditingQuote(quote);
            setWizardOpen(true);
        } else if (action === 'download') {
            setDownloadingQuote(quote);
        } else if (action === 'delete') {
            if (window.confirm("Are you sure you want to delete this quotation?")) {
                await QuotationService.deleteQuotation(quote.id);
                fetchQuotations();
            }
        }
    };

    return (
        <Box>
            {/* Hidden Download Container */}
            {downloadingQuote && (
                <Box sx={{ position: 'fixed', left: '-10000px', top: 0 }}>
                    <div style={{ width: '210mm', minHeight: '297mm', background: 'white' }}>
                        {downloadingQuote.businessStructure?.pdfTitle === 'Business Engagement'
                            ? <BusinessEngagementTemplate ref={downloadRef} quotation={downloadingQuote} />
                            : <PdfTemplate ref={downloadRef} quotation={downloadingQuote} />
                        }
                    </div>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Quotation Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew}>
                    Create Quotation
                </Button>
            </Box>

            {loading ? (
                <Loader />
            ) : quotations.length === 0 ? (
                <EmptyState message="No quotations found" />
            ) : (
                <QuotationTable 
                    quotations={quotations} 
                    onAction={handleAction} 
                />
            )}

            <Dialog 
                fullScreen 
                open={wizardOpen} 
                onClose={() => setWizardOpen(false)} 
                TransitionComponent={Transition}
            >
                <DialogContent sx={{ bgcolor: '#f5f5f5' }}>
                    <QuotationWizard 
                        initialData={editingQuote} 
                        onSaveDraft={handleSaveDraft}
                        onFinish={handleFinish}
                        onClose={() => setWizardOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default QuotationManagementPage;
